import { IS_LOCAL_ENV } from "../../../shared/constants/environment";

export const EMBEDDED_CHILD_SOURCE = "ws-embedded-child";
export const EMBEDDED_PARENT_SOURCE = "ws-embedded-parent";

export const SUPPORT_REQUESTED_EVENT = "support:requested";

/**
 * Wire-protocol envelope between this embedded iframe (the child) and
 * the SDK (the parent).
 *
 * IMPORTANT — every change to this shape MUST be backwards-compatible:
 *
 * - new event names go in `payload.event`
 * - new per-event fields go in `payload.data`
 * - never rename, remove, or repurpose an existing field
 * - never tighten the meaning of an existing field
 *
 * Why: integrators ship the @wingspan/embedded-sdk bundle from their
 * own CDN / build pipeline, so a non-backcompat change here would
 * silently break every cached deployment in the field. There is no
 * version field by design — a version that depends on engineer
 * discipline to be bumped is worse than no version, because it lies
 * the moment someone forgets.
 *
 * If a hard break is ever truly necessary, mint a new source string
 * (e.g. "ws-embedded-child-v2") instead of evolving this one — the
 * existing source check on both sides naturally drops the new shape
 * for old code.
 */
export const sendMessageToParent = (event: string, data?: any) => {
  data = data || {};
  const message = JSON.stringify({
    source: EMBEDDED_CHILD_SOURCE,
    payload: {
      event,
      data
    }
  });

  const target = window.parent !== window ? window.parent : window;
  target.postMessage(message, "*");
};

const AUTH_HANDSHAKE_TIMEOUT_MS = 15000;

export type ParentHandshake = {
  token: string;
  isSupportHandlerProvided: boolean;
};

// Parents (the SDK) deliver the session token via postMessage instead of the
// iframe URL — URLs leak via server logs, Referer headers, browser history,
// and extensions. We emit `ready`, then wait for an `auth` message whose
// payload carries the token.
export const requestAuthTokenFromParent = (): Promise<ParentHandshake> =>
  new Promise((resolve, reject) => {
    if (window.parent === window) {
      reject(new Error("Embedded app must run inside an iframe"));
      return;
    }

    const timeout = window.setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error("Timed out waiting for auth token from parent"));
    }, AUTH_HANDSHAKE_TIMEOUT_MS);

    const handler = (event: MessageEvent) => {
      let parsed: any;
      try {
        parsed =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      if (
        !parsed ||
        parsed.source !== EMBEDDED_PARENT_SOURCE ||
        parsed.payload?.event !== "auth"
      ) {
        return;
      }

      const token = parsed.payload?.data?.token;
      if (typeof token !== "string" || token.length === 0) return;

      window.clearTimeout(timeout);
      window.removeEventListener("message", handler);
      resolve({
        token,
        isSupportHandlerProvided:
          parsed.payload?.data?.isSupportHandlerProvided === true
      });
    };

    window.addEventListener("message", handler);
    sendMessageToParent("ready");
  });

if (IS_LOCAL_ENV) {
  window.addEventListener("message", function (message) {
    if (message.origin === "http://localhost:3000") {
      try {
        const data = JSON.parse(message.data);
        if (data.source === EMBEDDED_CHILD_SOURCE) {
          console.log("ws-embedded-message", data);
          return;
        }
      } catch {}
    }
  });
}
